import React, { useEffect, useState, useRef, useCallback } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import {
    Autocomplete,
    Button,
    CardContent,
    Grid,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    TableBody,
    Pagination,
    Tooltip,
    IconButton,
    CircularProgress,
    Stack,
    MenuItem
} from '@mui/material';
import { gridSpacing } from 'store/constant';
import { MemberType } from 'types/sacp';
import { useNavigate, useParams } from 'react-router-dom';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import { useTheme } from '@mui/material/styles';
import useGetLocationList from 'hooks/consume_api/query/useGetLocationList';
import useAuth from 'hooks/useAuth';
import MemberDelete from './MemberDelete';
import useMemberList from 'hooks/consume_api/query/useMemberList';
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import { initiateMemberExport } from 'services/reactQueryservices';
import SearchIcon from '@mui/icons-material/Search';
import useUserPermission from 'hooks/consume_api/query/useUserPermission';
import Loader from 'ui-component/Loader';
import { isExportInProgress, runTrackedExport, subscribeToExportStatus } from '../exportStatus';

interface PermissionData {
    role_id: string;
    action_id: string;
    permission: boolean;
    module_id: string;
    module_name: string;
    action_name: string;
}

const MemberRegister = () => {
    const theme = useTheme();
    const { id: projectId } = useParams();
    const navigate = useNavigate();

    // Refs for cleanup and tracking
    const isMountedRef = useRef(true);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const permissionRequestRef = useRef<boolean>(false);

    const [reload, setReload] = useState<boolean>(false);
    const [projectCount, setProjectCount] = useState<number | null>(null);
    const handleReload = () => setReload(!reload);

    const [memberId, setMemberId] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const handleOpenModal = () => setOpen(true);
    const handleCloseModal = () => setOpen(false);

    // Permission states - SECURE BY DEFAULT
    const [canAddMember, setCanAddMember] = useState<boolean>(false);
    const [canEditMember, setCanEditMember] = useState<boolean>(false);
    const [canViewMember, setCanViewMember] = useState<boolean>(false);
    const [deletePermission, setDeletePermission] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState<boolean>(false);
    const [isNavigating, setIsNavigating] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const memberExportStatusKey = `sacp-member-export:${projectId || ''}`;

    const { mutate, actionData } = useUserPermission();
    const { user } = useAuth();

    // Cleanup on component unmount
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            // Cleanup timers
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            // Reset all states
            setCanAddMember(false);
            setCanEditMember(false);
            setCanViewMember(false);
            setDeletePermission(false);
            setIsInitialized(false);
            setHasInitialDataLoaded(false);
            setIsNavigating(false);
            permissionRequestRef.current = false;
        };
    }, []);

    useEffect(() => {
        const syncExportStatus = () => {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(memberExportStatusKey));
            }
        };

        syncExportStatus();

        return subscribeToExportStatus(memberExportStatusKey, syncExportStatus);
    }, [memberExportStatusKey]);

    // Debug logging
    useEffect(() => {
        if (isMountedRef.current) {
            console.log('Member Permission State:', {
                isInitialized,
                canAddMember,
                canEditMember,
                canViewMember,
                deletePermission,
                hasInitialDataLoaded,
                userRoles: user?.roles?.map((r: any) => r.name)
            });
        }
    }, [isInitialized, canAddMember, canEditMember, canViewMember, deletePermission, hasInitialDataLoaded, user]);

    // Debounced permission fetching function
    const fetchPermissions = useCallback(() => {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;

            if (user?.roles && !permissionRequestRef.current) {
                permissionRequestRef.current = true;
                const roleIds = user.roles.map((role: any) => role.id);
                console.log('Fetching member permissions for roles:', roleIds);
                mutate(roleIds);
            } else if (!user?.roles) {
                // If no user, set initialized with default values
                if (isMountedRef.current) {
                    setIsInitialized(true);
                }
            }
        }, 150); // 150ms debounce to prevent rapid calls
    }, [user, mutate]);

    // Fetch permissions when component mounts
    useEffect(() => {
        if (isMountedRef.current) {
            fetchPermissions();
        }

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [fetchPermissions]);

    // Process permission data with NEW LOGIC
    useEffect(() => {
        if (actionData && isMountedRef.current) {
            console.log('Member permission data received:', actionData);

            // Reset permission request flag
            permissionRequestRef.current = false;

            // Check for Member Register module permissions
            const memberPermissions = actionData.filter(
                (permission: PermissionData) => permission.module_name === 'Member Register'
            );

            console.log('Member Register permissions found:', memberPermissions);

            // NEW LOGIC BASED ON YOUR API RESPONSE PATTERNS

            // Case 1: No permissions at all for this module
            if (memberPermissions.length === 0) {
                console.log('No permissions found for Member Register module - granting full access');
                setCanAddMember(true);
                setCanEditMember(true);
                setCanViewMember(true);
            }
            // Case 2: Only View permission exists (like Admin role 88)
            else if (memberPermissions.length === 1 && memberPermissions[0].action_name === 'View') {
                console.log('Only View permission found for Member Register - granting full access');
                setCanAddMember(true);
                setCanEditMember(true);
                setCanViewMember(memberPermissions[0].permission);
            }
            // Case 3: Multiple permissions exist (like role 91)
            else {
                // Find specific permissions
                const viewPermission = memberPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'View'
                );
                const addPermission = memberPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'Add'
                );
                const editPermission = memberPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'Edit'
                );

                console.log('Specific permissions found:', {
                    view: viewPermission?.permission,
                    add: addPermission?.permission,
                    edit: editPermission?.permission
                });

                // Set permissions based on API data
                setCanViewMember(viewPermission ? viewPermission.permission : false);
                setCanAddMember(addPermission ? addPermission.permission : false);
                setCanEditMember(editPermission ? editPermission.permission : false);
            }

            // Always check for admin role as a safety fallback
            const isAdmin = user?.roles?.some((item: any) =>
                item.name === 'FAO_ADMIN' || item.name === 'ADMIN'
            );

            if (isAdmin) {
                console.log('Admin user detected - ensuring full access');
                setCanAddMember(true);
                setCanEditMember(true);
                setCanViewMember(true);
            }

            // Mark permissions as initialized
            if (isMountedRef.current) {
                setIsInitialized(true);
            }
        }
    }, [actionData, user]);

    // Set delete permission based on FAO_ADMIN role
    useEffect(() => {
        if (isMountedRef.current && user) {
            const isFaoAdmin = user.roles.some((item: any) => item.name === 'FAO_ADMIN');
            setDeletePermission(isFaoAdmin);
        }
    }, [user]);

    const [selectedData, setSelectedData] = useState<MemberType | null>(null);

    const [location, setLocation] = useState<string>('-1');
    const [divisionId, setDivisionId] = useState<string>('');
    const [districtId, setDistrictId] = useState<string>('');
    const [upazilaId, setUpazilaId] = useState<string>('');
    const [unionId, setUnionId] = useState<string>('');

    const { data: divisionData } = useGetLocationList(location);
    const { data: distirctData } = useGetLocationList(divisionId);
    const { data: upazilaData } = useGetLocationList(districtId);
    const { data: unionData } = useGetLocationList(upazilaId);

    const [page, setPage] = useState<number>(1);
    const [row, setRow] = useState<number>(30);
    const [search, setSearch] = useState<string>('');
    const [jumpPage, setJumpPage] = useState<string>('');
    const [finalSearch, setFinalSearch] = useState<string>('');

    const { data: memberData, isFetching: memFetch, error } = useMemberList(
        page,
        row,
        divisionId,
        districtId,
        upazilaId,
        unionId,
        finalSearch,
        projectCount ? projectCount : 0
    );

    // Track when initial data is loaded
    useEffect(() => {
        if (isMountedRef.current && memberData && !hasInitialDataLoaded && !memFetch) {
            console.log('Initial member data loaded');
            setHasInitialDataLoaded(true);
        }
    }, [memberData, memFetch, hasInitialDataLoaded]);

    const formId = process.env.REACT_APP_MEMBER_FORM_ID;
    const xformId = process.env.REACT_APP_MEMBER_XFORM_ID;

    const submissionUrl = `/project/${projectId}/form/${formId}/${xformId}/new-submission`;
    const editUrl = `/project/${projectId}/form/${formId}/${xformId}/edit-submission`;
    const profileUrl = `/project/${projectId}/sacp/member/profile`;

    // Helper function to verify permissions directly from user roles
    const verifyUserPermission = useCallback((action: 'add' | 'edit' | 'delete' | 'view'): boolean => {
        if (!user?.roles) return false;

        // Check if user is admin
        const isAdmin = user.roles.some((role: any) =>
            role.name === 'FAO_ADMIN' || role.name === 'ADMIN'
        );

        if (isAdmin) {
            console.log('Admin user detected - granting member permission for:', action);
            return true;
        }

        // For non-admin users, use state permissions
        switch (action) {
            case 'add':
                return canAddMember;
            case 'edit':
                return canEditMember;
            case 'delete':
                return deletePermission;
            case 'view':
                return canViewMember;
            default:
                return false;
        }
    }, [user, canAddMember, canEditMember, deletePermission, canViewMember]);

    // Secure add member function with double verification
    const handleAddClick = () => {
        // Prevent multiple rapid clicks
        if (isNavigating) {
            console.log('Navigation already in progress');
            return;
        }

        setIsNavigating(true);

        // Double-check permissions before navigating
        const hasPermission = verifyUserPermission('add');

        if (!hasPermission) {
            console.warn('Permission denied for adding members. User roles:', user?.roles);
            setIsNavigating(false);
            return;
        }

        console.log('Permission granted for adding member. Navigating...');

        // Small delay to ensure state consistency
        setTimeout(() => {
            if (isMountedRef.current) {
                navigate(submissionUrl);
            }
            setIsNavigating(false);
        }, 50);
    };

    // Secure edit member function
    const handleEditClick = (itemId: string) => {
        if (isNavigating) return;

        setIsNavigating(true);

        const hasPermission = verifyUserPermission('edit');

        if (!hasPermission) {
            console.warn('Permission denied for editing members');
            setIsNavigating(false);
            return;
        }

        setTimeout(() => {
            if (isMountedRef.current) {
                navigate(`${editUrl}/${itemId}`);
            }
            setIsNavigating(false);
        }, 50);
    };

    const handleDelete = (id: string | number) => {
        const hasPermission = verifyUserPermission('delete');

        if (!hasPermission) {
            console.warn('Permission denied for deleting members');
            return;
        }

        setMemberId(id.toString());
        handleOpenModal();
    };

    interface FieldOption {
        id: number;
        field_name: string;
        field_parent_id: number;
        field_type_id: number;
        geocode: string;
        geojson: object;
        uploaded_file_path: string;
        created_by: number;
        updated_by: number;
        created_date: string;
        updated_date: string;
        longitude: number | null;
        latitude: number | null;
        field_name_bn: string | null;
    }

    const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    const handleRowChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setRow(Number(event.target.value));
    };

    const handleSearch = () => {
        setFinalSearch(search);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const designation = (is_cashier: boolean | null,
        is_president: boolean | null, is_secretary: boolean | null) => {
        if (is_cashier) return "Cashier";
        else if (is_president) return "President";
        else if (is_secretary) return "Secretary";
        return "";
    }

    const handleExport = async () => {
        if (isExportInProgress(memberExportStatusKey)) {
            setIsExporting(true);
            return;
        }

        try {
            setIsExporting(true);
            await runTrackedExport(memberExportStatusKey, () =>
                initiateMemberExport(divisionId, districtId, upazilaId, unionId, finalSearch, projectCount ? projectCount : 0)
            );
        } catch (error) {
            console.error('Member export failed:', error);
        } finally {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(memberExportStatusKey));
            }
        }
    }

    // Show loader until permissions AND initial data are resolved
    if (!isInitialized || !hasInitialDataLoaded) {
        console.log('Showing loader for MemberRegister - Waiting for:', {
            permissions: !isInitialized,
            data: !hasInitialDataLoaded
        });
        return <Loader />;
    }

    // Check if user has view permission
    if (!canViewMember) {
        return (
            <MainCard title="Access Denied">
                <CardContent>
                    <Typography variant="body1" color="error">
                        You do not have permission to view Member Register.
                    </Typography>
                </CardContent>
            </MainCard>
        );
    }

    console.log('Rendering Member MainCard with permissions:', {
        canAddMember,
        canEditMember,
        canViewMember,
        deletePermission
    });

    return (
        <MainCard
            content={false}
            contentSX={{ padding: 0 }}
            sx={{ padding: 0 }}
            title={
                <Grid container alignItems="center" justifyContent="space-between" spacing={gridSpacing}>
                    <Grid item>
                        <Typography variant="h3">Member List</Typography>
                    </Grid>
                    <Grid item>
                        {/* Show Add New Member button only if verified permissions */}
                        {canAddMember && (
                            <Button
                                sx={{ ml: -1 }}
                                color="secondary"
                                variant="contained"
                                onClick={handleAddClick}
                                disabled={isNavigating}
                                startIcon={<LibraryAddOutlinedIcon fontSize="small" />}
                            >
                                {isNavigating ? 'Processing...' : 'Add New Member'}
                            </Button>
                        )}

                        {/* Export button - always visible if user can view */}
                        {canViewMember && (
                            <Button
                                sx={{ ml: 1 }}
                                color="secondary"
                                variant="contained"
                                onClick={handleExport}
                                disabled={isNavigating || isExporting}
                                startIcon={isExporting ? <CircularProgress color="inherit" size={16} /> : undefined}
                            >
                                {isExporting ? 'Preparing...' : 'Export'}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            }
        >
            {/* Show loader for subsequent data fetches */}
            {memFetch ? (
                <Loader />
            ) : memberData?.data.length !== 0 ? (
                <>
                    <CardContent>
                        <Grid container spacing={gridSpacing} marginBottom="15px">
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={divisionData?.data as FieldOption[]}
                                    getOptionLabel={(option) => option.field_name}
                                    value={divisionData?.data.find((item: any) => item.id.toString() === divisionId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setDivisionId(value?.id.toString());
                                            setDistrictId('');
                                            setUpazilaId('');
                                            setUnionId('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select division" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={(distirctData?.data as FieldOption[]) ?? []}
                                    getOptionLabel={(option) => option.field_name}
                                    value={distirctData?.data.find((item: any) => item.id.toString() === districtId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setDistrictId(value?.id.toString());
                                            setUpazilaId('');
                                            setUnionId('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select district" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={(upazilaData?.data as FieldOption[]) ?? []}
                                    getOptionLabel={(option) => option.field_name}
                                    value={upazilaData?.data.find((item: any) => item.id.toString() === upazilaId) || null}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setUpazilaId(value?.id.toString());
                                            setUnionId('');
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select upazila" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={(unionData?.data as FieldOption[]) ?? []}
                                    getOptionLabel={(option) => option.field_name}
                                    value={unionData?.data.find((item: any) => item.id.toString() === unionId) || null}
                                    onChange={(event, value) => {
                                        if (value) setUnionId(value?.id.toString());
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select union" variant="outlined" />}
                                />
                            </Grid>
                            <Grid item lg={3} sm={6}>
                                <Autocomplete
                                    options={[1, 2, 3, 4, 5]}
                                    getOptionLabel={(option) => option.toString()}
                                    value={projectCount}
                                    onChange={(event, value) => {
                                        setProjectCount(value);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Project Count" variant="outlined" />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
                            <Grid item>
                                <TextField
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    value={search}
                                    id="outlined-basic"
                                    label="search"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item>
                                <IconButton onClick={handleSearch} color="primary">
                                    <SearchIcon />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper}>
                            <Table sx={{ width: '100%' }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">#</TableCell>
                                        <TableCell align="left">Name</TableCell>
                                        <TableCell align="left">Gender</TableCell>
                                        <TableCell align="left">Group</TableCell>
                                        <TableCell align="left">MemberId</TableCell>
                                        <TableCell align="left">Address</TableCell>
                                        <TableCell align="left">Contact</TableCell>
                                        <TableCell align="left">Designation</TableCell>
                                        <TableCell align="left">Project Count</TableCell>
                                        <TableCell align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {memberData?.data?.data.map((item: any, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell align="left">{(page - 1) * row + index + 1}</TableCell>
                                            <TableCell align="left">{item.name}</TableCell>
                                            <TableCell align="left">{item.sex}</TableCell>
                                            <TableCell align="left">{item.group}</TableCell>
                                            <TableCell align="left">{item.member_id}</TableCell>
                                            <TableCell align="left">{item.address}</TableCell>
                                            <TableCell align="left">{item.contact}</TableCell>
                                            <TableCell align="left">
                                                {designation(item.is_cashier, item.is_president, item.is_secretary)}
                                            </TableCell>
                                            <TableCell align="left">{item.projectCount}</TableCell>
                                            <TableCell align="left">
                                                <Stack direction="row">
                                                    {/* Show Edit button only if verified permissions */}
                                                    {canEditMember && (
                                                        <Tooltip placement="top" title="Edit">
                                                            <IconButton
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.warning.dark,
                                                                    borderColor: theme.palette.warning.main,
                                                                    '&:hover ': { background: theme.palette.warning.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => handleEditClick(item.id)}
                                                                disabled={isNavigating}
                                                            >
                                                                <BorderColorTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Profile button - always visible if can view */}
                                                    {canViewMember && (
                                                        <Tooltip placement="top" title="Profile">
                                                            <IconButton
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.success.dark,
                                                                    borderColor: theme.palette.success.main,
                                                                    '&:hover ': { background: theme.palette.success.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => {
                                                                    navigate(`${profileUrl}?instance=${item.id}&benNo=${item.member_id}&name=${item.name}&group=${item.group}&address=${item.address}&fathersname=${item.fithersname}&phone=${item.contact}`);
                                                                }}
                                                                disabled={isNavigating}
                                                            >
                                                                <PersonOutlineIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Delete button - only for FAO_ADMIN */}
                                                    {deletePermission && (
                                                        <Tooltip placement="top" title="Delete">
                                                            <IconButton
                                                                sx={{
                                                                    color: 'red',
                                                                    borderColor: theme.palette.success.main,
                                                                    '&:hover ': { background: theme.palette.success.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => handleDelete(item.id)}
                                                                disabled={isNavigating}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Grid container spacing={2} alignItems="center" marginTop="10px">
                            <Grid item>
                                <Typography variant="h4">
                                    Total : {memberData?.data.totalCount}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <TextField
                                    select
                                    label="Rows per page"
                                    value={row}
                                    onChange={handleRowChange}
                                    sx={{ width: '120px' }}
                                >
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={30}>30</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item>
                                <TextField
                                    type="number"
                                    label="Go to page"
                                    value={jumpPage}
                                    onChange={(e) => setJumpPage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const p = parseInt(jumpPage);
                                            const maxPage = Math.ceil(memberData?.data.totalCount / row);
                                            if (p >= 1 && p <= maxPage) {
                                                setPage(p);
                                                setJumpPage('');
                                            }
                                        }
                                    }}
                                    inputProps={{ min: 1, max: Math.ceil(memberData?.data.totalCount / row) }}
                                    sx={{ width: '120px' }}
                                />
                            </Grid>
                            <Grid item>
                                <Pagination
                                    count={Math.ceil(memberData?.data.totalCount / row)}
                                    color="primary"
                                    page={page}
                                    onChange={handleChangePage}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </>
            ) : (
                <CardContent>
                    <Typography variant="body1">No Member data found</Typography>
                </CardContent>
            )}

            {open && <MemberDelete id={memberId} handleReload={handleReload} open={open} onClose={handleCloseModal} />}
        </MainCard>
    );
};

export default MemberRegister;
